import Icon from '../Icon/Icon';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../containers/UseAuth';
import { useService } from '../../containers/UseService';
import { fromStatusToLinkPath } from '../../utils/functions';

const leftMenuVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: '-100%' },
};

function LeftMenu({ isMenuOpen = false, openMenu = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [showModalContinueCampaign, setShowModalContinueCampaign] =
        useState(false);
    const toggleModalContinueCampaign = () =>
        setShowModalContinueCampaign(!showModalContinueCampaign);
    const service = useService();
    const campaignsSelector = useSelector((state) => state.campaigns);
    const statuses = campaignsSelector.statuses
        ? campaignsSelector.statuses
        : [];
    const [isCampaignOpen, setIsCampaignOpen] = useState(false);
    const [isProposalOpen, setIsProposalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState(statuses);
    const statusesInLocalStorage = localStorage.getItem('statuses')
        ? JSON.parse(localStorage.getItem('statuses'))
        : [];
    const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
    const wallet = useSelector((state) => state.wallet.budgets);
    const [count, setCount] = useState(null);

    useEffect(() => {
        if (!wallet) {
            setIsCampaignsLoading(true);
        } else {
            setIsCampaignsLoading(false);
        }
    }, [wallet]);

    useEffect(async () => {
        try {
            const campaigns = await service.getCampaignsPerStatus();
            setNewStatus(campaigns);
        } catch (error) {
            console.log(error);
        }
    }, []);

    const toggleMenu = () => setIsCampaignOpen(!isCampaignOpen);
    const toggleMenuProposal = () => setIsProposalOpen(!isProposalOpen);
    const keepMenuOpen = () => setIsCampaignOpen(true);
    const openCampaignsMenu = () => {
        openMenu();
        setIsCampaignOpen(true);
    };
    const openProposalsMenu = () => {
        openMenu();
        setIsProposalOpen(true);
    };

    const onClickNewCampaign = () => {
        setIsCampaignOpen(!isCampaignOpen);
        service.clearNewCampaignData();
        goToNewCampaign();
    };

    const goToNewCampaign = () => {
        const routeToNewCampaign = '/campaign/new';
        const routeToEditCampaign = '/campaign/edit';

        if (location.pathname.includes(routeToEditCampaign)) {
            window.location.href = routeToNewCampaign;
        } else {
            if (location.pathname === routeToNewCampaign) {
                history.go(); //refresh
            } else {
                history.push('/campaign/new');
            }
        }
    };

    useEffect(() => {
        const getNewCount = async () => {
            try {
                const response = await service.getCampaignProposalCount();
                setCount(response);
            } catch {}
        };
        getNewCount();
    }, []);

    const auth = useAuth();
    return (
        <>
            <ul className="list-none w-100 p-4 relative overflow-hidden">
                <li className="linkLeft my-4 h-10">
                    <NavLink
                        to="/dashboard"
                        className={`${isMenuOpen ? 'block' : 'hidden'} text-sm`}>
                        {t('common.menu.dashboard')}
                        <br />
                        <span className="font-thin text-gray-400 text-xs">
                            {newStatus['ACTIVE']}{' '}
                            {t('common.menu.activeCampaigns')}
                        </span>
                    </NavLink>
                    <NavLink to="/dashboard">
                        <Icon
                            name="analytics"
                            className="ml-2"
                        />
                    </NavLink>
                </li>
                <li className={`linkLeft my-4 h-10`}>
                    <NavLink
                        to="/management"
                        className={`${isMenuOpen ? 'block' : 'hidden'} text-sm`}>
                        {t('common.menu.management')}
                    </NavLink>
                    <NavLink to="/management">
                        <Icon
                            name="cog"
                            className="ml-2"
                        />
                    </NavLink>
                </li>
                {isCampaignsLoading && (
                    <li className="flex justify-between content-center font-light text-sm my-2 mt-4 mb-8 text-gray-600 cursor-default">
                        <span
                            className={`${isMenuOpen ? 'block' : 'hidden'} text-sm`}>
                            {t('common.words.loading')}...
                        </span>
                    </li>
                )}
                {!isCampaignsLoading && (
                    <div className="mt-4 mb-8">
                        <li className="linkLeft my-4">
                            <button
                                className={`${isMenuOpen ? 'block' : 'hidden'} text-sm`}
                                onClick={toggleMenu}>
                                {t('common.menu.campaigns')}{' '}
                                <Icon
                                    name="chevron-down"
                                    className={`transition-transform duration-150 transform text-xs ${
                                        isCampaignOpen
                                            ? 'rotate-0'
                                            : 'rotate-90'
                                    }`}
                                />
                            </button>
                            <button
                                type="button"
                                onClick={openCampaignsMenu}
                                className="ml-2"
                                aria-label={t('common.menu.campaigns')}>
                                <Icon
                                    name="bullseye"
                                />
                            </button>
                        </li>

                        {isCampaignOpen && (
                            <>
                                <motion.ul
                                    className={`list-none w-100 pl-4 py-2 overflow-hidden ${
                                        isMenuOpen
                                            ? 'block opacity-100'
                                            : 'hidden opacity-0'
                                    }`}
                                    animate={isCampaignOpen ? 'open' : 'closed'}
                                    variants={leftMenuVariants}>
                                    <li
                                        className="link text-sm"
                                        onClick={onClickNewCampaign}>
                                        {t('common.menu.newCampaign')}
                                    </li>
                                    {Object.keys(statusesInLocalStorage).map(
                                        (status, index) => (
                                            <li
                                                className="linkLeft"
                                                key={index}
                                                onClick={keepMenuOpen}>
                                                <NavLink
                                                    to={`/campaigns/status/${fromStatusToLinkPath(
                                                        status
                                                    )}`}
                                                    className={
                                                        location.pathname ===
                                                        `/campaigns/status/${fromStatusToLinkPath(
                                                            status
                                                        )}`
                                                            ? 'font-bold'
                                                            : ''
                                                    }>
                                                    {t(
                                                        `common.campaign.detail.${status}`
                                                    )}
                                                </NavLink>
                                                {statusesInLocalStorage[status]}
                                            </li>
                                        )
                                    )}
                                </motion.ul>
                            </>
                        )}
                    </div>
                )}
                {auth.isSupervising() && (
                    <li className="linkLeft">
                        <button
                            className={`${isMenuOpen ? 'block' : 'hidden'} text-sm`}
                            onClick={toggleMenuProposal}>
                            {t('common.menu.proposals')}{' '}
                            <Icon
                                name="chevron-down"
                                className={`transition-transform duration-150 transform text-xs ${
                                    isProposalOpen ? 'rotate-0' : 'rotate-90'
                                }`}
                            />
                        </button>
                        <button
                            type="button"
                            onClick={openProposalsMenu}
                            className="ml-2"
                            aria-label={t('common.menu.proposals')}>
                            <Icon
                                name="file"
                                style={{ opacity: 0.7 }}
                            />
                        </button>
                    </li>
                )}
                {isProposalOpen && (
                    <motion.ul
                        className={`list-none w-100 pl-4 py-2 overflow-hidden ${
                            isMenuOpen
                                ? 'block opacity-100'
                                : 'hidden opacity-0'
                        }`}
                        animate={isProposalOpen ? 'open' : 'closed'}
                        variants={leftMenuVariants}>
                        <li className="link text-sm">
                            <NavLink
                                to="#"
                                className={''}
                                onClick={service.createCampaignProposalName}>
                                {t('common.menu.newProposal')}
                            </NavLink>
                        </li>
                        {count &&
                            count.data.campaign.map((status) => {
                                return (
                                    <li className="linkLeft" key={status}>
                                        <NavLink
                                            to={`/campaign-proposals/status/${status.status}`}
                                            className="flex justify-between w-full">
                                            <span>
                                                {t(
                                                    `common.campaign-proposals.statuses.${status.status}`
                                                )}
                                            </span>
                                            <span>{status.count}</span>
                                        </NavLink>
                                    </li>
                                );
                            })}
                    </motion.ul>
                )}
            </ul>
        </>
    );
}

export default LeftMenu;
